// src/components/tools/TurningArcTool/index.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { ActionBar, Button, HStack, Popover, Portal, Separator, Text, VStack } from "@chakra-ui/react";
import { Tooltip } from "../../ui/tooltip";
import { useAppStore } from "../../../context/store";
import { matchKeyInText } from "../../../utilies/Utility";
import { useMapObjects } from "../../../context/MapObjectsContext";

import { LuCornerUpRight, LuInfo, LuUndo2, LuX } from "react-icons/lu";
import { toggleTurningArcTool, undoTurningArc } from "../../../utilies/turningArcTools/Add";

const CURBSTONE_KEY = "curbstone";
const ROADEDGE_KEY = "roadedge";
const CONCRETE_BARRIER_KEY = "concretebarrier";
const GUARDRAIL_KEY = "guardrail";
const LANE_MARKER_KEY = "lanemarker";
const VIRTUAL_LINE_KEY = "virtualline";
const TURNING_LINE_KEY = "turningline";
const TURNING_ARC_KEY = "turningarc";

const ALLOWED_KEYS = [CURBSTONE_KEY, ROADEDGE_KEY, GUARDRAIL_KEY, CONCRETE_BARRIER_KEY, LANE_MARKER_KEY, VIRTUAL_LINE_KEY, TURNING_LINE_KEY, TURNING_ARC_KEY];

function TurningArcTool() {
  const viewerRef = useAppStore((s) => s.viewerRef);
  const editMode = useAppStore((s) => s.editMode);
  const originalCameraControls = useAppStore((s) => s.originalCameraControls);
  const userData = useAppStore((s) => s.userData);

  const turningArcMode = useAppStore((s) => s.turningArcMode);
  const setTurningArcMode = useAppStore((s) => s.setTurningArcMode);

  const { mapObjects, setMapObjects, mapObjectsRef } = useMapObjects();

  const [isShow, setIsShow] = useState(false);

  const dsRef = useRef({
    curbstone: null,
    roadedge: null,
    guardrail: null,
    concretebarrier: null,

    lanemarker: null,
    virtualline: null,
    turningline: null,
    turningarc: null,
  });

  const findDataSources = useCallback(() => {
    const t = {
      curbstone: null,
      roadedge: null,
      guardrail: null,
      concretebarrier: null,

      lanemarker: null,
      virtualline: null,
      turningline: null,
      turningarc: null,
    };

    for (const key in mapObjects) {
      const obj = mapObjects[key];
      if (!obj?.file_name) continue;

      if (matchKeyInText(obj.file_name, CURBSTONE_KEY)) t.curbstone = obj.dataSource;
      if (matchKeyInText(obj.file_name, ROADEDGE_KEY)) t.roadedge = obj.dataSource;
      if (matchKeyInText(obj.file_name, GUARDRAIL_KEY)) t.guardrail = obj.dataSource;
      if (matchKeyInText(obj.file_name, CONCRETE_BARRIER_KEY)) t.concretebarrier = obj.dataSource;

      if (matchKeyInText(obj.file_name, LANE_MARKER_KEY)) t.lanemarker = obj.dataSource;
      if (matchKeyInText(obj.file_name, VIRTUAL_LINE_KEY)) t.virtualline = obj.dataSource;
      if (matchKeyInText(obj.file_name, TURNING_LINE_KEY)) t.turningline = obj.dataSource;
      if (matchKeyInText(obj.file_name, TURNING_ARC_KEY)) t.turningarc = obj.dataSource;
    }

    return t;
  }, [mapObjects]);

  useEffect(() => {
    const t = findDataSources();
    dsRef.current = t;

    const hasAny = Boolean(t.curbstone || t.roadedge || t.guardrail || t.concretebarrier || t.lanemarker || t.virtualline || t.turningline || t.turningarc);

    setIsShow(Boolean(hasAny));
  }, [findDataSources]);

  const disableTool = useCallback(() => {
    const { curbstone, roadedge, guardrail, concretebarrier, lanemarker, virtualline, turningline, turningarc } = dsRef.current || {};

    try {
      toggleTurningArcTool(viewerRef.current, null, true, curbstone, roadedge, guardrail, concretebarrier, lanemarker, virtualline, turningline, turningarc, originalCameraControls);
    } catch {}
  }, [viewerRef, originalCameraControls]);

  const enableTool = useCallback(() => {
    const t = findDataSources();
    dsRef.current = t;

    const hasAny = Boolean(t.curbstone || t.roadedge || t.guardrail || t.concretebarrier || t.lanemarker || t.virtualline || t.turningline || t.turningarc);

    if (!hasAny) {
      setTurningArcMode(false);
      disableTool();
      return false;
    }

    toggleTurningArcTool(
      viewerRef.current,
      (targetKey) => {
        const key = typeof targetKey === "string" ? targetKey.toLowerCase() : "";
        if (!ALLOWED_KEYS.includes(key)) return;

        setMapObjects((prev) => {
          const arr = Array.isArray(prev) ? prev : Object.values(prev || {});
          const idx = arr.findIndex((o) => o?.file_name && matchKeyInText(o.file_name, key));
          if (idx < 0) return prev;

          const cur = arr[idx];
          if (cur?.is_edited === true) return prev;

          const next = arr.slice();
          next[idx] = { ...cur, is_edited: true };
          return next;
        });
      },
      false,
      t.curbstone,
      t.roadedge,
      t.guardrail,
      t.concretebarrier,
      t.lanemarker,
      t.virtualline,
      t.turningline,
      t.turningarc,
      originalCameraControls,
      () => setTurningArcMode(false), // NEW: sync UI when ESC disables tool
      userData,
      mapObjectsRef, setMapObjects
     
    );

    return true;
  }, [findDataSources, viewerRef, setTurningArcMode, disableTool, setMapObjects, originalCameraControls]);

  const toggleFully = useCallback(
    (force) => {
      const next = typeof force === "boolean" ? force : !turningArcMode;

      if (next) {
        const ok = enableTool();
        if (ok) setTurningArcMode(true);
      } else {
        setTurningArcMode(false);
        disableTool();
      }
    },
    [turningArcMode, enableTool, disableTool, setTurningArcMode],
  );

  useEffect(() => {
    if (!turningArcMode) disableTool();
  }, [turningArcMode, disableTool]);

  useEffect(() => {
    if (!isShow && turningArcMode) toggleFully(false);
  }, [isShow, turningArcMode, toggleFully]);

  useEffect(() => {
    return () => disableTool();
  }, [disableTool]);

  if (!isShow) return null;

  return (
    <>
      <HStack>
        <Button
          disabled={editMode}
          p={0}
          bg={turningArcMode ? "green" : "white"}
          color={turningArcMode ? "white" : "MenuText"}
          borderWidth={turningArcMode ? 2 : 1}
          borderColor={turningArcMode ? "green" : "gray.200"}
          onClick={() => toggleFully()}
          aria-pressed={turningArcMode}
          title="Turning Arc"
        >
          <Tooltip content={"Turning Arc"}>
            <LuCornerUpRight size={22} />
          </Tooltip>
        </Button>
      </HStack>

      <ActionBar.Root open={turningArcMode} closeOnEscape={false} closeOnInteractOutside={false}>
        <ActionBar.Positioner>
          <ActionBar.Content>
            <VStack gap={0} align="flex-start">
              <HStack gap={0}>
                <Popover.Root positioning={{ placement: "top-center" }}>
                  <Popover.Trigger asChild>
                    <Button size="sm" variant="unstyled" bg="transparent" border="none" p={0} m={0}>
                      <LuInfo />
                    </Button>
                  </Popover.Trigger>
                  <Portal>
                    <Popover.Positioner shadow={"lg"}>
                      <Popover.Content width={"fit-content"}>
                        <Popover.Arrow />
                        <Popover.Body py={3} px={5}>
                          pick start and end points, then press a key to choose the target layer and create the arc.
                        </Popover.Body>
                      </Popover.Content>
                    </Popover.Positioner>
                  </Portal>
                </Popover.Root>
                <Text fontWeight="semibold" fontSize="sm">
                  Turning Arc Tool
                </Text>
              </HStack>

              <Separator my={2} width={"full"} />
              <HStack>
                <Button disabled={editMode || !turningArcMode} p={0} bg={"white"} color={"MenuText"} borderWidth={1} borderColor={"gray.200"} onClick={() => undoTurningArc()}>
                  <Tooltip content={"Undo"}>
                    <LuUndo2 size={22} />
                  </Tooltip>
                </Button>

                <Button disabled={editMode || !turningArcMode} p={0} colorPalette={"red"} variant="outline" onClick={() => toggleFully()}>
                  <Tooltip content={"Close"}>
                    <LuX />
                  </Tooltip>
                </Button>
              </HStack>
            </VStack>
          </ActionBar.Content>
        </ActionBar.Positioner>
      </ActionBar.Root>
    </>
  );
}

export default TurningArcTool;
